import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import moment from 'moment'
import { isoFromDateAndHHmm } from '../../../../pages/Dispatch/lib/orderDisplay'
import { LIFECYCLES } from '../../../../pages/Dispatch/lib/lifecycles'
import { resolveActorEmail } from '../../../../utils/currentActor'
import { writeBatch, doc, onSnapshot, updateDoc, addDoc, collection, deleteDoc, getDocs, getDoc, query, orderBy, setDoc, where } from 'firebase/firestore'
import { db, getProjectCollection } from '../../../../services/firebase/firebaseConfig'
import type { Route, Vehicle, DriverOption, JobStatus } from '../../../../pages/Dispatch/optimizer/lib/types'
import { isPseudoRoute } from '../../../../pages/Dispatch/optimizer/lib/types'
import { normalizeRoutes } from '../../../../pages/Dispatch/optimizer/lib/normalizeRoutes'
import { normalizeOrder } from '../../../../pages/Dispatch/lib/normalizeOrder'
import { enrichEditorRowProducts } from '../../../../pages/Dispatch/lib/orderEditorAdapter'
import { selectGlobalUnit } from '../selectors'
import { optimizerRegistry } from '../../../../pages/Dispatch/optimizer/lib/optimizerRegistry'
import { calculateRoute } from '../../../../pages/Dispatch/optimizer/lib/osrm'
import { buildOptimizePayload } from '../../../../pages/Dispatch/optimizer/lib/optimizePayload'
import { collectIncompleteAddressStops, formatIncompleteAddressError } from '../../../../pages/Dispatch/optimizer/lib/addressValidation'
import { isHiddenBucket } from '../../../../pages/Dispatch/optimizer/lib/hiddenBuckets'
import { sortStopsPreservingHubs, type BucketSortKey } from '../../../../pages/Dispatch/optimizer/lib/bucketSort'
import { mergeExcludedIntoUnfit, OPTIMIZE_MULTISTOP_MSG, UNFIT_STOPS_VEHICLE, expandToLinkedGroupIds, collectGroupStops } from '../../../../pages/Dispatch/optimizer/lib/linkedStops'
// The user-visible error toast lives on the dashboard slice (Dispatch/index.tsx
// renders s.dashboard.error). Optimizer's own setError is not shown anywhere, so
// block messages route through here to actually reach the user.
import { setError as setDashboardError } from '../dashboardSlice/dashboardSlice'
import { buildOrderUpdateFields } from '../../../../pages/Dispatch/optimizer/lib/orderUpdateFields'
import { buildGeocodeFirestoreFields } from '../../../../pages/Dispatch/optimizer/lib/geocodePatch'
import { FLEET_COLLECTION, toFleetDoc, toFleetUpdate } from '../../../../pages/Dispatch/optimizer/lib/fleetDoc'
import { applyRouteOrder } from './selectors'
import { orderRoutesForBoard } from '../../../../pages/Dispatch/optimizer/lib/normalizeRoutes'

// Loose root accessors (dashboardSlice uses the same `any` pattern for cross-slice reads).
type AppDispatch = (action: any) => any
type GetState = () => any

export interface HistorySnapshot { id?: string; Action: string; Routes: any[]; User: string; TimeStamp: any }

export interface JobRun { id?: string; JobID: string; NumberOfOrders: number; User: string; CreatedAt: any; Region: string; SelectedDate: string }

export interface OptimizerState {
  _active: boolean
  selectedDate: string | null
  region: string
  mode: 'Logistics' | 'Installations'
  routes: Route[]
  vehicles: Vehicle[]
  drivers: DriverOption[]
  selectedClients: string[]
  missingVehicles: string[]
  distributionZones: any[]
  job: { id: string | null; status: JobStatus; engine: 'jsprit' | 'routific'; error: string | null }
  optimizeSettings: {
    optimizationMode: 'minVehicle' | 'shortestDistance' | 'balanced'
    startWorkingTime: string
    endWorkingTime: string
    avoidToll: boolean
    minVisitsPerVehicle: number | null
    selectedVehicles: string[]
    selectedClients: string[]
  }
  selectedStopId: string | null
  bulkSelection: string[]
  drawing: { active: boolean; selectedStopIds: string[] }
  routeVisibility: Record<string, boolean>
  timelineCollapsed: boolean
  loading: { routes: boolean; fleet: boolean }
  error: string | null
  needsReload: boolean
  editRequestId: string | null
  editingStopId: string | null
  history: HistorySnapshot[]
  /** Transient: excluded stops stashed at submit time so the async poll path can merge them back. Cleared after merge or on next submit. */
  pendingExcludedStops: any[]
  movePinMode: boolean
  jobRuns: JobRun[]
  routeColors: Record<string, string>
  /** Session-only column order for real-vehicle columns (drag reorder). Empty = default order. */
  routeOrder: string[]
  /** Stop id for which the time-lock modal is open; null = closed. */
  timeLockStopId: string | null
  /** Vehicle whose route is currently calculating/optimizing (drives the
   *  per-column progress bar); null = idle. */
  busyVehicle: string | null
}

export const initialOptimizerState: OptimizerState = {
  _active: false,
  selectedDate: null,
  region: '',
  mode: 'Logistics',
  routes: [],
  vehicles: [],
  drivers: [],
  selectedClients: [],
  missingVehicles: [],
  distributionZones: [],
  job: { id: null, status: 'idle', engine: 'jsprit', error: null },
  optimizeSettings: {
    optimizationMode: 'balanced', startWorkingTime: '', endWorkingTime: '',
    avoidToll: true, minVisitsPerVehicle: null, selectedVehicles: [], selectedClients: [],
  },
  selectedStopId: null,
  bulkSelection: [],
  drawing: { active: false, selectedStopIds: [] },
  routeVisibility: {},
  timelineCollapsed: false,
  loading: { routes: false, fleet: false },
  error: null,
  needsReload: false,
  editRequestId: null,
  editingStopId: null,
  history: [],
  pendingExcludedStops: [],
  movePinMode: false,
  jobRuns: [],
  routeColors: {},
  routeOrder: [],
  timeLockStopId: null,
  busyVehicle: null,
}

/** A route's depot endpoints — a stop never belongs before the START hub or after
 *  the END hub. Hub stops carry orderType 'HUB' (id may start with START/END). */
const isHubStop = (st: any): boolean =>
  !!st && (st.orderType === 'HUB' || (typeof st.id === 'string' && (st.id.startsWith('START') || st.id.startsWith('END'))))

/** Clamp an insert index so a moved stop lands BETWEEN a leading and a trailing
 *  hub. Routes without hubs clamp to [0, len] (no-op). */
const clampBetweenHubs = (stops: any[], idx: number): number => {
  const lo = stops.length > 0 && isHubStop(stops[0]) ? 1 : 0
  const hi = stops.length > 0 && isHubStop(stops[stops.length - 1]) ? stops.length - 1 : stops.length
  return Math.max(lo, Math.min(idx, hi))
}

/** An empty board column for a fleet vehicle that has no orders. Carries the
 *  vehicle's depot hubs (so drag-clamp + depot marker work) and its driver, so
 *  every region vehicle/driver shows as a column even with nothing assigned. */
const buildEmptyVehicleRoute = (v: any): Route => {
  const hasGeo = (loc: any) => !!loc?.geocodes && typeof loc.geocodes.lat === 'number'
  const hub = (prefix: string, loc: any): any => ({
    id: `${prefix}***${loc?.name || 'HUB'}`,
    invoice: 'HUB', vehicle: 'HUB', client: '', customer: '', driver: '',
    orderType: 'HUB', orderTypeShort: 'H',
    geocodes: loc?.geocodes ?? { lat: 0, long: 0 },
    startWindow: '', endWindow: '', serviceTime: 15, distributionZone: loc?.name || '',
  })
  const stops = hasGeo(v.startLocation) && hasGeo(v.endLocation)
    ? [hub('START', v.startLocation), hub('END', v.endLocation)]
    : []
  return {
    vehicle: v.name, driver: v.driver ?? null, stops,
    visible: true, isTruckLocked: false, deliveryAssignee: null,
    optimized: false, showRoute: false, shouldCheckRoute: false,
  } as Route
}

/** Add an empty column for every fleet vehicle that isn't already a route. */
const withVehicleColumns = (routes: Route[], vehicles: any[]): Route[] => {
  const present = new Set(routes.map((r) => r.vehicle))
  for (const v of vehicles ?? []) {
    if (!v?.name || present.has(v.name) || isPseudoRoute(v.name)) continue
    routes.push(buildEmptyVehicleRoute(v))
    present.add(v.name)
  }
  return routes
}

/**
 * Match a driver across the two id schemes in play: getRoutes seeds `driver.id`
 * with the USER doc id, while a driver picked from the region dropdown carries the
 * driverAppUserName as its id. So email (then name) is the stable cross-source key
 * — an id-only comparison fails to recognise the same person and leaves the driver
 * showing on two vehicles.
 */
const isSameDriver = (a: any, b: any): boolean => {
  if (!a || !b) return false
  const ea = (a.email || '').toString().trim().toLowerCase()
  const eb = (b.email || '').toString().trim().toLowerCase()
  if (ea && eb) return ea === eb
  if (a.id && b.id && a.id === b.id) return true
  const na = (a.name || '').toString().trim().toLowerCase()
  const nb = (b.name || '').toString().trim().toLowerCase()
  return !!na && na === nb
}

/**
 * True when `driver` currently holds a LOCKED route other than `exceptVehicle`.
 * A locked truck means "leave this route alone", so its driver must not be
 * reassigned out from under it — the alternative (leaving them on both) would put
 * two drivers on one vehicle, which the getFleets join resolves arbitrarily.
 */
const driverIsOnLockedRoute = (s: OptimizerState, driver: any, exceptVehicle: string): boolean => {
  if (!driver) return false
  return s.routes.some(
    (r) => r.vehicle !== exceptVehicle && r.isTruckLocked && isSameDriver(r.driver, driver),
  )
}

const slice = createSlice({
  name: 'optimizer',
  initialState: initialOptimizerState,
  reducers: {
    setActive: (s, a: PayloadAction<boolean>) => { s._active = a.payload },
    setSelectedDate: (s, a: PayloadAction<string>) => { s.selectedDate = a.payload },
    setRegion: (s, a: PayloadAction<string>) => { s.region = a.payload },
    setMode: (s, a: PayloadAction<'Logistics' | 'Installations'>) => { s.mode = a.payload },
    setRoutes: (s, a: PayloadAction<Route[]>) => {
      const routes = normalizeRoutes({ ...emptyResp(), routes: a.payload }).routes
      // Show a column for every fleet vehicle, even with no orders (requires
      // s.vehicles to be set first — loadRoutes dispatches setVehicles before this).
      s.routes = withVehicleColumns(routes, s.vehicles)
    },
    setVehicles: (s, a: PayloadAction<Vehicle[]>) => { s.vehicles = a.payload },
    setDrivers: (s, a: PayloadAction<DriverOption[]>) => { s.drivers = a.payload },
    setSelectedClients: (s, a: PayloadAction<string[]>) => { s.selectedClients = a.payload },
    setMissingVehicles: (s, a: PayloadAction<string[]>) => { s.missingVehicles = a.payload },
    setDistributionZones: (s, a: PayloadAction<any[]>) => { s.distributionZones = a.payload },
    setSelectedStop: (s, a: PayloadAction<string | null>) => { s.selectedStopId = a.payload },
    setBulkSelection: (s, a: PayloadAction<string[]>) => { s.bulkSelection = a.payload },
    setOptimizeSettings: (s, a: PayloadAction<Partial<OptimizerState['optimizeSettings']>>) => {
      s.optimizeSettings = { ...s.optimizeSettings, ...a.payload }
    },
    setJob: (s, a: PayloadAction<Partial<OptimizerState['job']>>) => { s.job = { ...s.job, ...a.payload } },
    setRouteVisibility: (s, a: PayloadAction<Record<string, boolean>>) => { s.routeVisibility = a.payload },
    setDrawing: (s, a: PayloadAction<Partial<OptimizerState['drawing']>>) => { s.drawing = { ...s.drawing, ...a.payload } },
    setTimelineCollapsed: (s, a: PayloadAction<boolean>) => { s.timelineCollapsed = a.payload },
    setLoading: (s, a: PayloadAction<Partial<OptimizerState['loading']>>) => { s.loading = { ...s.loading, ...a.payload } },
    setError: (s, a: PayloadAction<string | null>) => { s.error = a.payload },
    resetOptimizer: () => initialOptimizerState,
    assignStopToRoute: (s, a: PayloadAction<{ stopId: string; toVehicle: string; toIndex: number }>) => {
      const { stopId, toVehicle, toIndex } = a.payload
      // Resolve everything BEFORE mutating: the stop used to be spliced out first,
      // so an unknown destination dropped it from every route.
      let source: any = null
      let sourceIndex = -1
      for (const r of s.routes) {
        const i = r.stops.findIndex((x) => x.id === stopId)
        if (i >= 0) { source = r; sourceIndex = i; break }
      }
      if (!source) return
      // A hub is the route's depot, not an order — it never moves. Hub ids also
      // repeat across columns sharing a depot, so the findIndex above can match a
      // different route's hub and splice out the wrong one.
      if (isHubStop(source.stops[sourceIndex])) return
      const dest = s.routes.find((r) => r.vehicle === toVehicle)
      if (!dest) return

      const moved = source.stops.splice(sourceIndex, 1)[0]
      source.shouldCheckRoute = true
      source.optimized = false
      moved.vehicle = toVehicle
      dest.stops.splice(clampBetweenHubs(dest.stops, toIndex), 0, moved)
      dest.shouldCheckRoute = true
      dest.optimized = false
      // Bring the rest of the dragged stop's linked group along — even >2-stop
      // groups — inserting them right after the seed (Vue onUnpublishedChange).
      const groupIds = expandToLinkedGroupIds(s.routes, [stopId])
      if (groupIds.size > 1) {
        for (const r of s.routes) {
          if (r.vehicle === toVehicle) continue
          if ((r.stops || []).some((x: any) => x.orderType !== 'HUB' && groupIds.has(x.id))) { r.shouldCheckRoute = true; r.optimized = false }
        }
        const members = collectGroupStops(s.routes, groupIds, toVehicle)
        if (members.length) {
          const pos = dest.stops.findIndex((x) => x.id === stopId)
          dest.stops.splice(pos + 1, 0, ...members)
        }
      }
    },
    // Re-order a bucket's real stops by an order attribute (Ready To Route sort
    // control). HUB depots stay pinned; a bucket carries no route sequence, so this
    // is display-only and never flags shouldCheckRoute.
    sortBucketStops: (s, a: PayloadAction<{ vehicle: string; key: BucketSortKey }>) => {
      const r = s.routes.find((x) => x.vehicle === a.payload.vehicle)
      if (!r) return
      r.stops = sortStopsPreservingHubs(r.stops, a.payload.key)
    },
    resequenceStop: (s, a: PayloadAction<{ vehicle: string; fromIndex: number; toIndex: number }>) => {
      const r = s.routes.find((x) => x.vehicle === a.payload.vehicle)
      if (!r) return
      // The depot hubs bracket the route and stay put — clampBetweenHubs only
      // constrains where a stop lands, not what may be picked up.
      if (isHubStop(r.stops[a.payload.fromIndex])) return
      const [m] = r.stops.splice(a.payload.fromIndex, 1)
      r.stops.splice(clampBetweenHubs(r.stops, a.payload.toIndex), 0, m)
      r.shouldCheckRoute = true
      r.optimized = false
    },
    setRouteTotals: (s, a: PayloadAction<{ vehicle: string; stops: any[]; totalDistance: number; totalTime: number }>) => {
      const r = s.routes.find((x) => x.vehicle === a.payload.vehicle)
      if (!r) return
      r.stops = a.payload.stops
      r.totalDistance = a.payload.totalDistance
      r.totalTime = a.payload.totalTime
      r.shouldCheckRoute = false
    },
    setRouteDriver: (s, a: PayloadAction<{ vehicle: string; driver: any }>) => {
      const { vehicle, driver } = a.payload
      // Taking a driver off a LOCKED route would edit a route the user pinned, so
      // refuse the whole assignment rather than silently stripping it. The pickers
      // disable these drivers, so this is the backstop.
      if (driverIsOnLockedRoute(s, driver, vehicle)) return
      // Assign on the route (publishRoutesSelective reads route.driver to write the
      // order's vehicle-driver fields).
      const r = s.routes.find((x) => x.vehicle === vehicle); if (r) r.driver = driver
      // Mirror onto the fleet vehicle — UpdateDataModal (publish) reads vehicle.driver
      // for the driver label and the hasDriver / push-to-driver gate. Vue's
      // updateDriver writes the same { driver, driverName } onto the vehicle.
      const v = s.vehicles.find((x) => x.name === vehicle)
      if (v) { v.driver = driver ?? null; v.driverName = driver?.name ?? '' }
      // A driver can only drive one vehicle: clear them from any OTHER vehicle/route
      // (Vue nulls the previous vehicle's driver/driverName before reassigning).
      // Match across id schemes (see isSameDriver) so a dropdown pick also clears a
      // getRoutes-seeded DEFAULT driver on another vehicle.
      if (driver) {
        s.vehicles.forEach((ov) => {
          if (ov.name !== vehicle && isSameDriver(ov.driver, driver)) { ov.driver = null; ov.driverName = '' }
        })
        s.routes.forEach((orr) => {
          if (orr.vehicle !== vehicle && isSameDriver(orr.driver, driver)) { orr.driver = null }
        })
      }
    },
    /**
     * Set a vehicle's DEFAULT driver (fleet config) without touching the board.
     * The routes on screen came from getRoutes and keep their driver until the user
     * reloads — persistVehicleDriver flags needsReload so they're told to. Contrast
     * setRouteDriver, which is the board's own per-route assignment.
     */
    setVehicleDriver: (s, a: PayloadAction<{ vehicleId: string; driver: any }>) => {
      const { vehicleId, driver } = a.payload
      if (driverIsOnLockedRoute(s, driver, '')) return
      const v = s.vehicles.find((x) => x.id === vehicleId)
      if (!v) return
      v.driver = driver ?? null
      v.driverName = driver?.name ?? ''
      // One vehicle per driver: drop them from whichever vehicle held them before.
      if (driver) {
        s.vehicles.forEach((ov) => {
          if (ov.id !== vehicleId && isSameDriver(ov.driver, driver)) { ov.driver = null; ov.driverName = '' }
        })
      }
    },
    setRouteShift: (s, a: PayloadAction<{ vehicle: string; shiftStart?: string; shiftEnd?: string }>) => {
      const v = s.vehicles.find((x) => x.name === a.payload.vehicle); if (v) {
        if (a.payload.shiftStart) v.shiftStart = a.payload.shiftStart
        if (a.payload.shiftEnd) v.shiftEnd = a.payload.shiftEnd
      }
      const r = s.routes.find((x) => x.vehicle === a.payload.vehicle); if (r) r.shouldCheckRoute = true
    },
    toggleRouteLock: (s, a: PayloadAction<string>) => {
      const r = s.routes.find((x) => x.vehicle === a.payload); if (r) r.isTruckLocked = !r.isTruckLocked
    },
    reverseRoute: (s, a: PayloadAction<string>) => {
      const r = s.routes.find((x) => x.vehicle === a.payload); if (!r) return
      if (r.stops.length < 2) return
      const head = r.stops[0]?.orderType === 'HUB' ? [r.stops[0]] : []
      const tail = r.stops[r.stops.length - 1]?.orderType === 'HUB' ? [r.stops[r.stops.length - 1]] : []
      const mid = r.stops.slice(head.length, r.stops.length - tail.length).reverse()
      r.stops = [...head, ...mid, ...tail]
      r.shouldCheckRoute = true
      r.optimized = false
    },
    setRouteVisibilityOne: (s, a: PayloadAction<{ vehicle: string; visible: boolean }>) => {
      s.routeVisibility[a.payload.vehicle] = a.payload.visible
    },
    addVehicles: (s, a: PayloadAction<any[]>) => { s.vehicles.push(...a.payload) },
    updateVehicleField: (s, a: PayloadAction<{ id: string; field: string; value: any }>) => {
      const v = s.vehicles.find((x) => x.id === a.payload.id); if (v) (v as any)[a.payload.field] = a.payload.value
    },
    deleteVehicles: (s, a: PayloadAction<string[]>) => {
      const ids = new Set(a.payload)
      const removedNames = s.vehicles.filter((v) => ids.has(v.id)).map((v) => v.name)
      s.vehicles = s.vehicles.filter((v) => !ids.has(v.id))
      const ready = s.routes.find((r) => r.vehicle === 'ReadyToRoute')
      for (const name of removedNames) {
        const route = s.routes.find((r) => r.vehicle === name)
        if (route) { if (ready) ready.stops.push(...route.stops); s.routes = s.routes.filter((r) => r.vehicle !== name) }
      }
      s.needsReload = true
    },
    setNeedsReload: (s, a: PayloadAction<boolean>) => { s.needsReload = a.payload },
    requestEditStop: (s, a: PayloadAction<string | null>) => { s.editRequestId = a.payload },
    setEditingStop: (s, a: PayloadAction<string | null>) => { s.editingStopId = a.payload },
    patchStop: (s, a: PayloadAction<{ stopId: string; patch: Record<string, any> }>) => {
      // Does NOT flag recompute — the caller (refreshStop) decides whether a route
      // recalculation is actually warranted (only when geocodes change).
      for (const r of s.routes) {
        const stop = r.stops.find((x) => x.id === a.payload.stopId)
        if (stop) { Object.assign(stop, a.payload.patch); break }
      }
    },
    setStopTimeLock: (s, a: PayloadAction<{ vehicle: string; stopId: string; lockedStartWindow: string; lockedEndWindow: string; timeLocked: boolean }>) => {
      for (const r of s.routes) {
        const stop = r.stops.find((x) => x.id === a.payload.stopId)
        if (stop) {
          stop.lockedStartWindow = a.payload.lockedStartWindow
          stop.lockedEndWindow = a.payload.lockedEndWindow
          stop.timeLocked = a.payload.timeLocked
          r.shouldCheckRoute = true
          break
        }
      }
    },
    setStopServiceTime: (s, a: PayloadAction<{ stopId: string; serviceTime: number; endWindow?: string }>) => {
      for (const r of s.routes) {
        const stop = r.stops.find((x) => x.id === a.payload.stopId)
        if (stop) {
          stop.serviceTime = a.payload.serviceTime
          if (a.payload.endWindow) stop.endWindow = a.payload.endWindow
          r.shouldCheckRoute = true
          r.optimized = false
          break
        }
      }
    },
    setMovePinMode: (s, a: PayloadAction<boolean>) => { s.movePinMode = a.payload },
    setStopGeocode: (s, a: PayloadAction<{ stopId: string; lat: number; long: number }>) => {
      for (const r of s.routes) {
        const stop = r.stops.find((x) => x.id === a.payload.stopId)
        if (stop) {
          stop.geocodes = { lat: a.payload.lat, long: a.payload.long }
          r.shouldCheckRoute = true
          r.optimized = false
          break
        }
      }
    },
    setHistory: (s, a: PayloadAction<HistorySnapshot[]>) => { s.history = a.payload },
    setJobRuns: (s, a: PayloadAction<JobRun[]>) => { s.jobRuns = a.payload },
    assignStopsBulk: (s, a: PayloadAction<{ stopIds: string[]; toVehicle: string }>) => {
      const dest = s.routes.find((r) => r.vehicle === a.payload.toVehicle)
      // A LOCKED destination can't receive stops — mirrors the board's
      // isDropDisabled (RouteColumn) so draw-assign honours the same lock as drag.
      if (!dest || dest.isTruckLocked) return
      // Expand the selection to whole linked groups so draw/assign moves every
      // linked order together (Vue parity), then move them (never the hubs).
      const ids = expandToLinkedGroupIds(s.routes, a.payload.stopIds)
      const moved: any[] = []
      for (const r of s.routes) {
        // Leave LOCKED source routes untouched — a locked route's cards can't be
        // dragged out (isDragDisabled), and draw-assign must respect the same lock
        // rather than ripping pinned stops (or a locked PreRoute bucket) out from
        // under it. A linked group split across a locked + unlocked route only
        // moves its unlocked members; the lock wins.
        if (r.isTruckLocked) continue
        const keep: any[] = []
        for (const stop of r.stops) {
          if (stop.orderType !== 'HUB' && ids.has(stop.id)) { stop.vehicle = a.payload.toVehicle; moved.push(stop); r.shouldCheckRoute = true; r.optimized = false } else keep.push(stop)
        }
        r.stops = keep
      }
      if (moved.length === 0) return
      dest.stops.splice(clampBetweenHubs(dest.stops, dest.stops.length), 0, ...moved)
      dest.shouldCheckRoute = true
      dest.optimized = false
    },
    setPendingExcludedStops: (s, a: PayloadAction<any[]>) => { s.pendingExcludedStops = a.payload },
    setRouteColor: (s, a: PayloadAction<{ vehicle: string; color: string }>) => { s.routeColors[a.payload.vehicle] = a.payload.color },
    setTimeLockStopId: (s, a: PayloadAction<string | null>) => { s.timeLockStopId = a.payload },
    setBusyVehicle: (s, a: PayloadAction<string | null>) => { s.busyVehicle = a.payload },
    reorderColumns: (s, a: PayloadAction<{ from: number; to: number }>) => {
      // Compute the current column order (applying any existing routeOrder),
      // splice from→to, then store the resulting name array. Filter hidden buckets
      // (PreRoute on MBE) so these indices match the rendered board (selectBoardRoutes
      // applies the same filter) — otherwise a column drag would splice at the wrong
      // slot. ReadyToRoute / Unfit and every vehicle stay reorderable.
      const board = applyRouteOrder(orderRoutesForBoard(s.routes as Route[]), s.routeOrder)
        .filter((r) => !isHiddenBucket(r.vehicle))
      const order = board.map((r) => r.vehicle)
      const [moved] = order.splice(a.payload.from, 1)
      order.splice(a.payload.to, 0, moved)
      s.routeOrder = order
    },
  },
})

const emptyResp = () => ({ drivers: [], vehicles: [], routes: [], selectedClients: [], distributionZones: [], missingVehicles: [] })

export const {
  setActive, setSelectedDate, setRegion, setMode, setRoutes, setVehicles, setDrivers, setSelectedClients,
  setMissingVehicles, setDistributionZones, setSelectedStop, setBulkSelection, setOptimizeSettings, setJob,
  setRouteVisibility, setDrawing, setTimelineCollapsed, setLoading, setError, resetOptimizer,
  assignStopToRoute, resequenceStop, sortBucketStops, setRouteTotals,
  setRouteDriver, setVehicleDriver, setRouteShift, toggleRouteLock, reverseRoute, setRouteVisibilityOne,
  addVehicles, updateVehicleField, deleteVehicles, setNeedsReload,
  requestEditStop, setEditingStop, patchStop,
  setStopTimeLock, setStopServiceTime, setMovePinMode, setStopGeocode,
  assignStopsBulk, setHistory,
  setPendingExcludedStops,
  setJobRuns,
  setRouteColor,
  setBusyVehicle,
  reorderColumns,
  setTimeLockStopId,
} = slice.actions

/** Firestore's hard cap on writes per batch. A batch is atomic — exceed it and the
 *  whole commit is rejected, so large publishes must be split. */
const FIRESTORE_BATCH_LIMIT = 500

/** Region option from the TMS_PORTAL user store (same source dashboardSlice reads). */
const findRegion = (state: any, id: string) =>
  (state.user?.regions ?? []).map((r: any) => r.value).find((v: any) => v?.id === id)

/** Load the day's routes via the getRoutes cloud function. */
export const loadRoutes = () => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  const state = getState()
  const opt: OptimizerState = state.optimizer
  if (!opt.region || !opt.selectedDate) return
  const region = findRegion(state, opt.region) ?? { id: opt.region, name: opt.region }
  // The order-query day window must be reckoned in the SELECTED REGION's timezone —
  // orders store StartWindow as the UTC instant of their local time in the zone's
  // TimeZone, so bucketing the day in any other tz drops same-date orders. Derive it
  // from the region's distribution zones (zone.value.Region === region id); fall back
  // to the browser tz (what the dashboard uses) only if zones aren't loaded.
  const regionTimeZone = (state.user?.distributionzones ?? [])
    .find((z: any) => z?.value?.Region === opt.region)?.value?.TimeZone
  const browserTimeZone = (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'America/Toronto'
  const timezone = region.timezone ?? regionTimeZone ?? browserTimeZone
  // A new fetch invalidates the previous selection — the selected stop belongs to the
  // old region/date and won't exist in the incoming routes.
  dispatch(setSelectedStop(null))
  dispatch(setLoading({ routes: true }))
  try {
    const res = await axios.post(process.env.REACT_APP_GET_ROUTES as string, {
      data: { date: opt.selectedDate, region, timezone, mode: opt.mode, viewMode: 'routing' },
    })
    // Tolerate both response shapes: flat deliveryData, or wrapped under `data`
    // (this backend wraps optimize/getRoutes results under `data` in some deploys —
    // loadFleet does the same). Without this, a wrapped response reads as no routes.
    const body = (res?.data && (res.data as any).routes === undefined && (res.data as any).data)
      ? (res.data as any).data
      : res.data
    const resp = normalizeRoutes(body)
    // Vehicles first: setRoutes synthesizes an empty column per fleet vehicle.
    dispatch(setVehicles(resp.vehicles ?? []))
    dispatch(setRoutes(resp.routes))
    dispatch(setDrivers(resp.drivers ?? []))
    dispatch(setSelectedClients(resp.selectedClients ?? []))
    dispatch(setMissingVehicles(resp.missingVehicles ?? []))
    dispatch(setDistributionZones(resp.distributionZones ?? []))
    dispatch(setError(null))
  } catch (e: any) {
    // When the server gives a definitive answer (e.g. getRoutes 400 "No orders"/"No
    // vehicles" for the new region/date), clear the previous region's routes/vehicles
    // so stale data isn't left on the board/map. On a transient network error (no
    // response) keep the current view rather than wiping the user's work.
    if (e?.response) {
      dispatch(setVehicles([]))
      dispatch(setRoutes([]))
      dispatch(setDrivers([]))
      dispatch(setSelectedClients([]))
      dispatch(setMissingVehicles([]))
      dispatch(setDistributionZones([]))
    }
    dispatch(setError(e?.response?.data?.message ?? e?.message ?? 'Failed to load routes'))
  } finally {
    dispatch(setLoading({ routes: false }))
  }
}

export const loadFleet = () => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  dispatch(setLoading({ fleet: true }))
  try {
    const state = getState()
    // Scope the fleet to the board's region — getFleets returns every vehicle in the
    // project otherwise. Same { id, name } region argument loadRoutes sends.
    const opt: OptimizerState = state.optimizer
    const region = opt.region ? (findRegion(state, opt.region) ?? { id: opt.region, name: opt.region }) : null
    const res = await axios.post(process.env.REACT_APP_GET_FLEETS as string, region ? { data: { region } } : {})
    // Tolerate response shapes: flat { vehicles }, wrapped { data: { vehicles } }
    // (same backend wraps optimize results under `data`), { fleets }, or a bare array.
    const body: any = res?.data ?? {}
    const vehicles = Array.isArray(body)
      ? body
      : body.vehicles ?? body.data?.vehicles ?? body.fleets ?? body.data?.fleets ?? []
    dispatch(setVehicles(vehicles))
    // getFleets also carries the region's distribution zones — they're what the
    // vehicle form offers as start/end depots. Only adopt a non-empty list:
    // getFleets answers with empty arrays when it has nothing to say, and this
    // runs while the board already holds the zones loadRoutes fetched.
    const zones = Array.isArray(body) ? [] : body.distributionZones ?? body.data?.distributionZones ?? []
    if (zones.length > 0) dispatch(setDistributionZones(zones))
    dispatch(setError(null))
  } catch (e: any) {
    dispatch(setError(e?.message ?? 'Failed to load fleet'))
  } finally {
    dispatch(setLoading({ fleet: false }))
  }
}

export const enterSingleDate = (regionId: string) => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  // Anything selected belongs to the mode being left. loadRoutes clears the selected
  // stop on its own; the bulk selection is this thunk's to drop.
  dispatch(setSelectedStop(null))
  dispatch(setBulkSelection([]))
  dispatch(setActive(true))
  if (!getState().optimizer.region && regionId) dispatch(setRegion(regionId))
  if (!getState().optimizer.selectedDate) dispatch(setSelectedDate(new Date().toISOString().slice(0, 10)))
  await dispatch(loadRoutes())
}

export const leaveSingleDate = () => (dispatch: AppDispatch): void => {
  optimizerRegistry.stopJob()
  dispatch(setActive(false))
  dispatch(resetOptimizer())
}

export const persistAssignment = ({ stopId, toVehicle }: { stopId: string; toVehicle: string }) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const routes = getState().optimizer.routes
    const dest = routes.find((r: any) => r.vehicle === toVehicle)
    const driverName = dest?.driver?.name ?? ''
    // Persist the whole linked group (the reducer already moved it in state), not
    // just the dragged stop, so the group assignment survives a reload.
    const ids = Array.from(expandToLinkedGroupIds(routes, [stopId]))
    try {
      const batch = writeBatch(db)
      for (const id of ids) batch.update(doc(db, 'TMS_ORDERS', id), { 'fields.Vehicle': toVehicle, 'fields.Driver': driverName })
      await batch.commit()
    } catch (e: any) {
      dispatch(setError(e?.message ?? 'Failed to save assignment'))
    }
  }

export const persistAssignmentBulk = ({ stopIds, toVehicle }: { stopIds: string[]; toVehicle: string }) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const routes = getState().optimizer.routes
    const dest = routes.find((r: any) => r.vehicle === toVehicle)
    const driverName = dest?.driver?.name ?? ''
    // Expand to whole linked groups so every moved linked order is persisted.
    const ids = Array.from(expandToLinkedGroupIds(routes, stopIds))
    try {
      const batch = writeBatch(db)
      for (const id of ids) batch.update(doc(db, 'TMS_ORDERS', id), { 'fields.Vehicle': toVehicle, 'fields.Driver': driverName })
      await batch.commit()
    } catch (e: any) {
      dispatch(setError(e?.message ?? 'Failed to save assignments'))
    }
  }

export const persistStopGeocode = ({ stopId, lat, long }: { stopId: string; lat: number; long: number }) =>
  async (dispatch: AppDispatch): Promise<void> => {
    try {
      const batch = writeBatch(db)
      batch.update(doc(db, 'TMS_ORDERS', stopId), buildGeocodeFirestoreFields(lat, long))
      await batch.commit()
    } catch (e: any) {
      dispatch(setError(e?.message ?? 'Failed to save geocode'))
    }
  }

export const persistVehicleField = ({ id, field, value }: { id: string; field: string; value: any }) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    dispatch(updateVehicleField({ id, field, value }))
    // The slice speaks getFleets' camelCase; FLEET stores something else entirely.
    const update = toFleetUpdate(field, value, getState().optimizer.distributionZones)
    // No FLEET column (returnToStart, say) means nothing durable changed, so the
    // board's routes are still valid and no reload is warranted.
    if (!update) return
    try {
      await updateDoc(doc(db, FLEET_COLLECTION, id), update)
      // The stored fleet no longer matches the plan getRoutes handed us.
      dispatch(setNeedsReload(true))
    } catch (e: any) {
      dispatch(setError(e?.message ?? 'Failed to update vehicle'))
    }
  }

/**
 * Best-effort: clear a driver's USERS.vehicle pointer. Never throws — releasing an
 * outgoing driver is cleanup around the real write (assigning the new driver, or
 * deleting the vehicle), and letting it fail that work is worse than a stale
 * pointer, which the getFleets join simply never matches.
 */
const releaseDriverVehicle = async (driverDocId: string): Promise<void> => {
  try {
    await updateDoc(doc(db, 'USERS', driverDocId), { vehicle: null })
  } catch (e) {
    console.error('Failed to release driver from vehicle:', driverDocId, e)
  }
}

/**
 * Assign (or clear) a vehicle's driver. FLEET has no driver column — getFleets joins
 * `USERS.vehicle == <FLEET doc id>` where isDriver — so the link is written on the
 * USERS side. `driver.id` is the USERS doc id, matching what getFleets returns.
 *
 * That join takes the first USER it finds for a vehicle, so the outgoing driver has
 * to be unpointed or the displayed driver becomes arbitrary. The reverse case needs
 * no cleanup: a driver holds one `vehicle`, so pointing them here removes them from
 * their previous vehicle by construction.
 */
export const persistVehicleDriver = (
  { vehicleId, driver }: { vehicleId: string; vehicleName?: string; driver: any },
) => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  const state = getState().optimizer
  const previous = state.vehicles.find((v: Vehicle) => v.id === vehicleId)?.driver
  if (isSameDriver(previous, driver) || (!previous && !driver)) return
  // Never strip a locked route's driver (see driverIsOnLockedRoute). The form
  // disables these options; bail here too so a stale render can't slip through.
  if (driverIsOnLockedRoute(state, driver, '')) return

  // Fleet state only — the board's routes keep the driver getRoutes gave them, and
  // needsReload prompts the user to refetch rather than the routes silently changing
  // under them.
  dispatch(setVehicleDriver({ vehicleId, driver }))
  // Unpointing the outgoing driver is best-effort cleanup: it must not decide
  // whether the NEW driver gets written. These were one sequential try, so a
  // previous id that no longer resolves threw and skipped the assignment entirely
  // while the UI showed it applied.
  if (previous?.id && previous.id !== driver?.id) {
    await releaseDriverVehicle(previous.id)
  }
  try {
    if (driver?.id) {
      await updateDoc(doc(db, 'USERS', driver.id), { vehicle: vehicleId })
    }
    dispatch(setNeedsReload(true))
  } catch (e: any) {
    dispatch(setError(e?.message ?? 'Failed to update driver'))
  }
}

/**
 * Delete a vehicle from the fleet. Firestore first, then local state: dropping the
 * row optimistically would leave the board disagreeing with the stored fleet if the
 * write is refused. The `deleteVehicles` reducer rehomes the vehicle's stops to
 * ReadyToRoute and flags the reload.
 */
export const persistDeleteVehicle = (id: string) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const vehicle = getState().optimizer.vehicles.find((v: Vehicle) => v.id === id)
    if (!vehicle) return
    try {
      await deleteDoc(doc(db, FLEET_COLLECTION, id))
      // Release the driver: their USERS.vehicle would otherwise point at a document
      // that no longer exists. Best-effort — the vehicle is already deleted, so a
      // failure here must not skip the local removal and strand the column on the
      // board.
      if (vehicle.driver?.id) {
        await releaseDriverVehicle(vehicle.driver.id)
      }
      dispatch(deleteVehicles([id]))
    } catch (e: any) {
      dispatch(setError(e?.message ?? 'Failed to delete vehicle'))
    }
  }

export const persistAddVehicle = (vehicle: any) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<string | null> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...fields } = vehicle || {}
    try {
      // The doc id is generated up front because the stored doc repeats it in `Id`.
      const ref = doc(collection(db, FLEET_COLLECTION))
      await setDoc(ref, toFleetDoc(fields, ref.id, getState().optimizer.distributionZones))
      dispatch(addVehicles([{ ...fields, id: ref.id }]))
      // A new vehicle only gets its board column from a getRoutes fetch.
      dispatch(setNeedsReload(true))
      // Returned so the caller can follow up with driver assignment, which needs the
      // new doc id to write onto USERS.
      return ref.id
    } catch (e: any) {
      dispatch(setError(e?.message ?? 'Failed to add vehicle'))
      return null
    }
  }

export const recomputeRoute = (vehicle: string) => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  const st: OptimizerState = getState().optimizer
  const route = st.routes.find((r: any) => r.vehicle === vehicle)
  if (!route) return
  // returnToStart lives on the fleet vehicle (Vue looks it up the same way);
  // the calculate backend needs it to decide whether to close the loop.
  const veh = st.vehicles.find((v: any) => v.name === vehicle)
  // The depot start/end times come from the fleet vehicle's shift, not from the
  // hub stops (those are empty on synthesized columns and stale after a shift
  // edit). Stamp the vehicle's shift onto the START hub (departure) and END hub
  // (return) so calculate schedules from the right clock — Vue carries the same
  // times on the hub stops it posts.
  const date = st.selectedDate || new Date().toISOString().slice(0, 10)
  const toIso = (v?: string): string | undefined => {
    if (!v) return undefined
    const s = String(v)
    if (s.includes('T')) return s                              // already an ISO datetime
    if (/^\d{1,2}:\d{2}/.test(s)) return isoFromDateAndHHmm(date, s)  // "HH:MM"
    return undefined
  }
  const startIso = toIso(veh?.shiftStart)
  const endIso = toIso(veh?.shiftEnd)
  const stops = route.stops.map((s: any, i: number, arr: any[]) => {
    if (s.orderType !== 'HUB') return s
    // START hub = depot departure. Vue's SET_HUB_START_END_TIME sets BOTH windows
    // to the shift-start datetime, so the backend schedules the first stop from
    // shift start whichever field it reads (it uses endWindow as the departure).
    if (i === 0 && startIso) return { ...s, startWindow: startIso, endWindow: startIso }
    // END hub = latest-return constraint. Vue deliberately does NOT rewrite the
    // END hub from shiftEnd (the real return is computed from the whole route),
    // so only fill empty windows (synthesized columns) — never clobber values the
    // backend already computed.
    if (i === arr.length - 1 && endIso) return { ...s, startWindow: s.startWindow || endIso, endWindow: s.endWindow || endIso }
    return s
  })
  dispatch(setBusyVehicle(vehicle))
  try {
    const res = await calculateRoute({ ...route, stops, returnToStart: veh?.returnToStart })
    dispatch(setRouteTotals({ vehicle, stops: res.stops, totalDistance: res.totalDistance, totalTime: res.totalTime }))
    dispatch(appendSnapshot(`Calculated ${vehicle}`))
  } catch (e: any) {
    dispatch(setError(e?.message ?? 'Failed to calculate route'))
  } finally {
    dispatch(setBusyVehicle(null))
  }
}

const STORE_STOP_MSG = 'Store-Stop must be picked up before its linked drop-off.'
const STORE_DROPOFF_MSG = 'Store-DropOff must occur after its linked store stop.'

/** Session-local time lock (mirrors Vue setTimeLock): validate linked orders, mutate, snapshot, recompute. */
export const commitTimeLock = ({ vehicle, stopId, hhmmStart, hhmmEnd, timeLocked }:
  { vehicle: string; stopId: string; hhmmStart: string; hhmmEnd: string; timeLocked: boolean }) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<{ ok: boolean; error?: string }> => {
    const st: OptimizerState = getState().optimizer
    const date = st.selectedDate || new Date().toISOString().slice(0, 10)
    const allStops = st.routes.flatMap((r) => r.stops)
    const stop = allStops.find((x) => x.id === stopId)
    if (!stop) return { ok: false, error: 'Stop not found' }

    let lockedStartWindow = ''
    let lockedEndWindow = ''
    if (timeLocked) {
      lockedStartWindow = isoFromDateAndHHmm(date, hhmmStart)
      lockedEndWindow = hhmmEnd
        ? isoFromDateAndHHmm(date, hhmmEnd)
        : moment.utc(lockedStartWindow)
            .add(process.env.REACT_APP_PROJECT_ID === 'shyftbase-proj2025-1' ? 1 : 3, 'hours')
            .format('YYYY-MM-DDTHH:mm') + ':00.000Z'

      if (stop.linkedOrder) {
        const linked = allStops.find((x) => x.id === stop.linkedOrder)
        if (linked) {
          const cur = moment.utc(lockedStartWindow)
          const lin = moment.utc(linked.startWindow)
          const ct = stop.orderType
          const lt = linked.orderType
          // Validate against the current stop's own type first (mirrors Vue setTimeLock),
          // then against the linked stop's type (covers locking a non-constrained stop
          // whose linked order is the Store-Stop/Store-DropOff).
          if (ct === 'Store-Stop' && lin.isValid() && cur.isAfter(lin)) return { ok: false, error: STORE_STOP_MSG }
          if (ct === 'Store-DropOff' && lin.isValid() && cur.isBefore(lin)) return { ok: false, error: STORE_DROPOFF_MSG }
          if (lt === 'Store-Stop' && cur.isValid() && lin.isAfter(cur)) return { ok: false, error: STORE_STOP_MSG }
          if (lt === 'Store-DropOff' && cur.isValid() && lin.isBefore(cur)) return { ok: false, error: STORE_DROPOFF_MSG }
        }
      }
    }

    dispatch(setStopTimeLock({ vehicle, stopId, lockedStartWindow, lockedEndWindow, timeLocked }))
    dispatch(appendSnapshot(`${timeLocked ? 'Locked' : 'Unlocked'} time for ${stop.invoice}`))
    // Planning board: only mark the route for recalculation (reducer sets
    // shouldCheckRoute). The user recalculates via Calculate/Optimize.
    return { ok: true }
  }

/** Session-local service-time change (mirrors Vue serviceTimeChange): recompute end window, snapshot, recompute route. */
export const commitServiceTime = ({ vehicle, stopId, serviceTime }:
  { vehicle: string; stopId: string; serviceTime: number }) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const st: OptimizerState = getState().optimizer
    const stop = st.routes.flatMap((r) => r.stops).find((x) => x.id === stopId)
    const endWindow = stop?.startWindow
      ? moment.utc(stop.startWindow).add(serviceTime, 'minutes').format('YYYY-MM-DDTHH:mm') + ':00.000Z'
      : undefined
    dispatch(setStopServiceTime({ stopId, serviceTime, endWindow }))
    dispatch(appendSnapshot(`Service time changed for ${vehicle}`))
    // Planning board: mark for recalculation only (no auto-calculate).
  }

/** Re-fetch a stop's order after an edit and patch the optimizer copy in place. */
export const refreshStop = (stopId: string) => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  try {
    // Use the same project-scoped collection the dashboard reads/writes (Today mode).
    const snap = await getDoc(doc(getProjectCollection('TMS_ORDERS'), stopId))
    const n = normalizeOrder({ id: (snap as any).id ?? stopId, data: () => (snap as any).data?.() ?? {} })
    const st: OptimizerState = getState().optimizer
    const route = st.routes.find((r: any) => r.stops.some((x: any) => x.id === stopId))
    const prev = route?.stops.find((x: any) => x.id === stopId)
    const geoChanged = !!prev && (prev.geocodes?.lat !== n.geocodes.lat || prev.geocodes?.long !== n.geocodes.long)
    // Enrich the (possibly thin) ProductJson from TMS_INVENTORY before deriving the
    // display totals, so a pure view+close reproduces what getRoutes computed at load.
    // Optimizer orders store a thin ProductJson line (InventoryId only — qty/weight/volume
    // live on the inventory doc); normalizeOrder alone yields productCount = line-count (→ 1)
    // and totalWeight/Volume = 0, which then clobbered the stop on close. Mirrors
    // getRoutes' server-side enrichment (functions OPTIMIZER/helpers/utils.ts).
    //
    // getRoutes stores stop.totalWeight/Volume (and productJson) in METRIC (dataSourceUnit);
    // the RouteCard converts to the selected unit at display. So enrich WITHOUT converting —
    // pass identity units (selected := dataSourceUnit) so fetchProductData's unitConverter is
    // a no-op — otherwise the card would convert an already-converted value twice.
    const identityUnit = (u: any) =>
      u?.dataSourceUnit ? { dataSourceUnit: u.dataSourceUnit, selected: u.dataSourceUnit } : u
    const units = {
      weight: identityUnit(selectGlobalUnit('Weight')(getState())),
      volume: identityUnit(selectGlobalUnit('Volume')(getState())),
      length: identityUnit(selectGlobalUnit('Length')(getState())),
    }
    const enriched = await enrichEditorRowProducts({ id: n.id, ProductJson: n.productJson }, units)
    const enrichedPj = Array.isArray(enriched.ProductJson) ? enriched.ProductJson : n.productJson
    // productCount is the SUM OF QUANTITIES (getRoutes.countProducts parity), not the
    // line count normalizeOrder returns.
    const productCount =
      enrichedPj.reduce((acc: number, p: any) => acc + (Number(p?.Quantity) || 1), 0) || n.productCount
    const totalWeight = enriched.TotalWeight ?? n.totalWeight
    const totalVolume = enriched.TotalVolume ?? n.totalVolume
    // Patch display/data fields only. Do NOT touch startWindow/endWindow — those are the
    // optimizer's COMPUTED schedule, not the order's requested window; overwriting them
    // (then recomputing) made the window visibly change and snap back on editor close.
    dispatch(patchStop({ stopId, patch: {
      geocodes: n.geocodes, address: n.address, orderStatus: n.OrderStatus, productCount,
      totalWeight, totalVolume, client: n.client, customer: n.customer, productJson: enrichedPj,
    } }))
    // Only recalculate the route when the stop's location actually moved — viewing an
    // order and closing it must not perturb the schedule.
    if (geoChanged && route && !isPseudoRoute(route.vehicle)) dispatch(recomputeRoute(route.vehicle))
  } catch (e: any) {
    dispatch(setError(e?.message ?? 'Failed to refresh stop'))
  }
}

/**
 * Merge `excludedStops` into the current Unfit Stops route and surface the
 * quarantine notice. Shared by the sync path (applyOptimizeResult) and the
 * async poll path (pollJobSolution). No-op when excludedStops is empty.
 */
const mergeExcludedAndSurface = (dispatch: AppDispatch, getState: GetState, excludedStops: any[]): void => {
  if (!excludedStops.length) return
  const state: OptimizerState = getState().optimizer
  const unfitRoute = state.routes.find((r) => r.vehicle === UNFIT_STOPS_VEHICLE)
  if (!unfitRoute) {
    console.warn('[optimizer] mergeExcludedAndSurface: excluded multi-stops could not be placed — no Unfit Stops route found.')
    return
  }
  const excludedIds = new Set(excludedStops.map((s) => s && s.id))
  const merged = mergeExcludedIntoUnfit(unfitRoute.stops as any[], excludedStops)
  const updatedRoutes = state.routes.map((r) => {
    if (r.vehicle === UNFIT_STOPS_VEHICLE) return { ...r, stops: merged }
    // MOVE (not copy) semantics: the quarantined stops must live ONLY in Unfit.
    // The async backend path replaces source routes with the response (which never
    // contained these), so this is a no-op there; the SYNC short-circuit paths
    // (nothing-to-optimize) never replace the source route, so strip the stops here
    // — otherwise they'd remain on the vehicle AND appear in Unfit (duplicated).
    const hasExcluded = (r.stops || []).some((s: any) => s && excludedIds.has(s.id))
    return hasExcluded ? { ...r, stops: (r.stops as any[]).filter((s: any) => !(s && excludedIds.has(s.id))) } : r
  })
  dispatch(setRoutes(updatedRoutes as Route[]))
  // Surface the quarantine notice via the existing error channel (no dedicated info
  // channel exists in the slice yet; see task-3-report.md for the concern).
  dispatch(setError(OPTIMIZE_MULTISTOP_MSG))
}

/** buildOptimizePayload artificially locks PreRoute (so the backend leaves its
 *  stops untouched); the result echoes that lock back. Restore PreRoute's
 *  pre-optimize lock state so the optimize result doesn't visibly lock the
 *  PreRoute bucket in the UI. */
const restorePreRouteLock = (getState: GetState, routes: any[]): any[] => {
  const prev = getState().optimizer.routes.find((r: any) => r.vehicle === 'PreRoute')
  const prevLocked = !!prev?.isTruckLocked
  return (routes || []).map((r: any) =>
    r && r.vehicle === 'PreRoute' ? { ...r, isTruckLocked: prevLocked } : r,
  )
}

/** Merge optimize-result routes into current state BY VEHICLE — keeping every
 *  route the response didn't include. Used by single-vehicle optimize, whose
 *  TSP response returns only the target route (a full setRoutes would wipe the
 *  other vehicle buckets). */
const mergeRoutesByVehicle = (getState: GetState, routes: any[]): any[] => {
  const current: any[] = getState().optimizer.routes ?? []
  const incoming = new Map((routes || []).map((r: any) => [r.vehicle, r]))
  const merged = current.map((r: any) => incoming.get(r.vehicle) ?? r)
  const known = new Set(current.map((r: any) => r.vehicle))
  for (const r of routes || []) if (r && !known.has(r.vehicle)) merged.push(r)
  return merged
}

const applyOptimizeResult = (
  dispatch: AppDispatch, getState: GetState, res: any, excludedStops: any[],
  opts: { mergeByVehicle?: boolean } = {},
): void => {
  const body = res?.data ?? {}
  // The server echoes the OptimizeCollection shape: the optimized routes live
  // under `data.routes`. Accept a flat `routes` too for older/other shapes.
  const routes = Array.isArray(body?.data?.routes) ? body.data.routes
    : Array.isArray(body?.routes) ? body.routes
      : null
  // Async job id: backend uses `jobId`; tolerate `job_id` and a nested form.
  const jobId = body?.jobId ?? body?.job_id ?? body?.data?.jobId ?? body?.data?.job_id ?? null
  if (routes) {
    // Single-vehicle optimize returns only the target route → merge it in,
    // keeping the other buckets. Bulk returns the whole board → replace.
    const next = opts.mergeByVehicle ? mergeRoutesByVehicle(getState, routes) : routes
    dispatch(setRoutes(restorePreRouteLock(getState, next)))
    // Merge held-back oversized linked groups into the Unfit Stops route (sync path).
    mergeExcludedAndSurface(dispatch, getState, excludedStops)
    // Clear stashed excludedStops so a later async optimize doesn't double-merge.
    dispatch(setPendingExcludedStops([]))
    dispatch(appendSnapshot(opts.mergeByVehicle ? 'Optimized vehicle' : 'Optimized routes'))
    dispatch(setJob({ status: 'success' }))
    return
  }
  if (jobId) {
    dispatch(setJob({ id: jobId, status: 'inProgress' }))
    dispatch(recordJobRun({ jobId }) as any)
    // Carry the merge-by-vehicle decision into the async path so a single-vehicle
    // optimize merges its one route instead of replacing the whole board.
    dispatch(pollJobSolution(jobId, opts.mergeByVehicle))
    return
  }
  dispatch(setJob({ status: 'error', error: 'No routes or job id returned' }))
}

export const submitOptimize = () => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  const state = getState()
  const region = findRegion(state, state.optimizer.region) ?? { id: state.optimizer.region, name: state.optimizer.region }
  const engine: 'jsprit' | 'routific' = state.optimizer.job.engine
  const url = engine === 'routific' ? process.env.REACT_APP_OPTIMIZE_ROUTIFIC : process.env.REACT_APP_OPTIMIZE_ROUTES
  dispatch(setJob({ status: 'submitting', error: null }))
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { excludedStops, payloadEmpty, payloadRoutes, ...payload } = buildOptimizePayload(state, region)
    // Block the run if any order that would be routed has an incomplete address or
    // an un-geocoded (0,0) location — the solver would otherwise place it at Null
    // Island. Hard block, list the offending invoices; nothing is sent.
    const badAddress = collectIncompleteAddressStops(payloadRoutes, [])
    if (badAddress.length > 0) {
      const msg = formatIncompleteAddressError(badAddress)
      dispatch(setDashboardError(msg))
      dispatch(setJob({ status: 'error', error: msg }))
      return
    }
    // Short-circuit: if every in-scope vehicle is left with only HUBs after quarantine,
    // skip the backend call — just merge excluded stops into Unfit and finish.
    if (payloadEmpty && excludedStops.length > 0) {
      dispatch(setPendingExcludedStops([]))
      mergeExcludedAndSurface(dispatch, getState, excludedStops)
      dispatch(setJob({ status: 'success' }))
      return
    }
    // Stash excludedStops in state BEFORE the async post resolves so the poll
    // callback can access them if the backend returns a job_id.
    dispatch(setPendingExcludedStops(excludedStops))
    const res = await axios.post(url as string, payload, { timeout: 900000 })
    // Bulk returns the WHOLE board (selected vehicles optimized, non-selected
    // emptied as their orders feed the pool, unassigned/unfit buckets) → replace.
    applyOptimizeResult(dispatch, getState, res, excludedStops)
  } catch (e: any) {
    dispatch(setJob({ status: 'error', error: e?.message ?? 'Optimize failed' }))
  }
}

export const optimizeSingleRoute = (vehicle: string) => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  const state = getState()
  const region = findRegion(state, state.optimizer.region) ?? { id: state.optimizer.region, name: state.optimizer.region }
  const engine: 'jsprit' | 'routific' = state.optimizer.job.engine
  const url = engine === 'routific' ? process.env.REACT_APP_OPTIMIZE_ROUTIFIC : process.env.REACT_APP_OPTIMIZE_ROUTES
  dispatch(setJob({ status: 'submitting', error: null }))
  // Drives the target column's progress bar (mirrors Vue's per-vehicle
  // `calculating` flag, which is set for both optimize and calculate).
  dispatch(setBusyVehicle(vehicle))
  // Pass [vehicle] as scopeVehicles so only the target vehicle's oversized linked
  // groups are quarantined — leaving every other vehicle's groups untouched (Vue
  // scopes splitOptimizePayload to [route.vehicle] for single-vehicle optimize).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { excludedStops, payloadEmpty, payloadRoutes, ...payload } = buildOptimizePayload(state, region, [vehicle])
  // Block the run if any of THIS vehicle's orders has an incomplete address or an
  // un-geocoded (0,0) location (same guard as bulk, scoped to the target vehicle).
  const badAddress = collectIncompleteAddressStops(payloadRoutes, [vehicle])
  if (badAddress.length > 0) {
    const msg = formatIncompleteAddressError(badAddress)
    dispatch(setDashboardError(msg))
    dispatch(setJob({ status: 'error', error: msg }))
    dispatch(setBusyVehicle(null))
    return
  }
  // `payload` IS the OptimizeCollection (options / data / isOptimizingBulk / …).
  // Its `data` is the OptimizerData (routes/vehicles/drivers).
  payload.selectedVehicles = [vehicle]
  payload.isOptimizingBulk = false
  // jsprit's TSP (single-vehicle) path keys off the vehicle name + its index in
  // the routes array it receives (backend OptimizeCollection.vehicle/vehicleIndex).
  ;(payload as any).vehicle = vehicle
  ;(payload as any).vehicleIndex = (payload.data.routes as any[]).findIndex((r) => r.vehicle === vehicle)
  // Short-circuit: if the TARGET vehicle has no optimizable (non-HUB) stops left
  // after quarantine, skip the backend call. Single-vehicle optimize is a TSP over
  // THIS vehicle's stops only — it never pulls from ReadyToRoute/Unfit — so an empty
  // target means there is nothing to optimize (e.g. every stop on it belongs to an
  // oversized >2-stop linked group that was just quarantined). Sending an empty
  // vehicle to the backend throws, so we just move the excluded stops to Unfit and
  // finish. (isPostExclusionPayloadEmpty also scans the unassigned buckets — correct
  // for BULK optimize, but wrong here: their stops aren't part of this vehicle's TSP.)
  const targetPayloadRoute = (payloadRoutes as any[]).find((r) => r && r.vehicle === vehicle)
  const targetHasStops = !!targetPayloadRoute && (targetPayloadRoute.stops || []).some((s: any) => s && s.orderType !== 'HUB')
  if (!targetHasStops) {
    dispatch(setPendingExcludedStops([]))
    if (excludedStops.length > 0) mergeExcludedAndSurface(dispatch, getState, excludedStops)
    dispatch(setJob({ status: 'success' }))
    dispatch(setBusyVehicle(null))
    return
  }
  // Stash excludedStops in state BEFORE the async post resolves so the poll
  // callback can access them if the backend returns a job_id.
  dispatch(setPendingExcludedStops(excludedStops))
  try {
    const res = await axios.post(url as string, payload, { timeout: 900000 })
    // Merge by vehicle: the single-vehicle response carries only the target
    // route, so keep every other bucket intact.
    applyOptimizeResult(dispatch, getState, res, excludedStops, { mergeByVehicle: true })
  } catch (e: any) {
    dispatch(setJob({ status: 'error', error: e?.message ?? 'Optimize failed' }))
  } finally {
    dispatch(setBusyVehicle(null))
  }
}

export const pollJobSolution = (jobId: string, mergeByVehicle = false) => (dispatch: AppDispatch, getState: GetState): void => {
  const unsub = onSnapshot(doc(db, 'OPTIMIZER_JOB_SOLUTIONS', jobId), (snap: any) => {
    const d = snap.data?.() ?? {}
    if (d.status === 'success' && d.solution) {
      // Single-vehicle optimize returns only the target route → merge by vehicle so
      // the other buckets survive. Bulk returns the whole board → replace outright.
      const next = mergeByVehicle ? mergeRoutesByVehicle(getState, d.solution) : d.solution
      dispatch(setRoutes(restorePreRouteLock(getState, next)))
      // Merge any quarantined multi-stop groups back into Unfit Stops (async path fix).
      const pendingExcluded: any[] = getState().optimizer.pendingExcludedStops ?? []
      mergeExcludedAndSurface(dispatch, getState, pendingExcluded)
      // Clear so a subsequent optimize doesn't double-merge stale data.
      dispatch(setPendingExcludedStops([]))
      dispatch(appendSnapshot('Optimized routes'))
      dispatch(setJob({ status: 'success' }))
      optimizerRegistry.stopJob()
    } else if (d.status === 'error') {
      dispatch(setJob({ status: 'error', error: d.errorMessage ?? 'Optimization failed' }))
      optimizerRegistry.stopJob()
    }
  })
  optimizerRegistry.startJob(unsub)
}

export const appendSnapshot = (action: string) => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  const st = getState().optimizer
  const user = getState().user?.email ?? ''
  if (!st.region || !st.selectedDate) return
  try {
    await addDoc(collection(db, `OPTIMIZER_SNAPSHOTS/${st.region}/${st.selectedDate}`), {
      Action: action, Routes: st.routes, User: user, TimeStamp: new Date().toISOString(),
    })
  } catch (e) { /* non-fatal */ console.error('appendSnapshot failed', e) }
}

export const restoreSnapshot = (index: number) => (dispatch: AppDispatch, getState: GetState): void => {
  const snap = getState().optimizer.history[index]
  if (snap?.Routes) dispatch(setRoutes(snap.Routes))
}

export const loadHistory = () => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  const st = getState().optimizer
  if (!st.region || !st.selectedDate) return
  try {
    const snap = await getDocs(query(collection(db, `OPTIMIZER_SNAPSHOTS/${st.region}/${st.selectedDate}`), orderBy('TimeStamp', 'desc')))
    dispatch(setHistory(snap.docs.map((d: any) => ({ id: d.id, ...d.data() }))))
  } catch (e: any) {
    dispatch(setError(e?.message ?? 'Failed to load history'))
  }
}

export const recordJobRun = ({ jobId }: { jobId: string }) =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const st = getState().optimizer
    const user = getState().user?.email ?? ''
    if (!st.region || !st.selectedDate || !jobId) return
    const numberOfOrders = st.routes
      .filter((r: any) => !isPseudoRoute(r.vehicle))
      .reduce((n: number, r: any) => n + (r.stops || []).filter((s: any) => s.orderType !== 'HUB').length, 0)
    try {
      await setDoc(doc(db, 'OPTIMIZER_JOB_IDS', jobId), {
        JobID: jobId,
        NumberOfOrders: numberOfOrders,
        User: user,
        CreatedAt: new Date(),
        Region: st.region,
        SelectedDate: st.selectedDate,
      })
    } catch (e: any) {
      dispatch(setError(e?.message ?? 'Failed to record job run'))
    }
  }

export const loadJobRuns = () =>
  async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const st = getState().optimizer
    if (!st.region || !st.selectedDate) return
    try {
      const snap = await getDocs(query(collection(db, 'OPTIMIZER_JOB_IDS'), where('SelectedDate', '==', st.selectedDate)))
      const rows: JobRun[] = snap.docs
        .map((d: any) => ({ id: d.id, ...(d.data() as any) }))
        .filter((r: any) => r.Region === st.region)
        .sort((a: any, b: any) => (b.CreatedAt?.toMillis?.() ?? 0) - (a.CreatedAt?.toMillis?.() ?? 0))
      dispatch(setJobRuns(rows))
    } catch (e: any) {
      dispatch(setError(e?.message ?? 'Failed to load job runs'))
    }
  }

export const refetchJobRun = (jobId: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    // Re-fetching a past run must not merge any in-session quarantined stops into
    // its solution — clear pendingExcludedStops so pollJobSolution's merge is a no-op.
    dispatch(setPendingExcludedStops([]))
    dispatch(setJob({ id: jobId, status: 'inProgress' }))
    dispatch(pollJobSolution(jobId) as any)
  }

/** Build an opsStatusIdMap from state.data.opsstatusesref (Status -> Firestore doc id). */
function buildOpsStatusIdMap(state: any): Map<string, string> {
  const opsstatusesref: any[] = state.data?.opsstatusesref ?? []
  const map = new Map<string, string>()
  opsstatusesref.forEach((entry: any) => {
    const v = entry?.value
    if (v?.Status && v?.Id) map.set(v.Status, v.Id)
  })
  return map
}

/** OrderStatus -> lifeCycle, from state.data.orderstatusesref — the same source the
 *  dispatch board's substatusMap is derived from (dashboardSlice/selectors). */
function buildStatusLifecycleMap(state: any): Map<string, string> {
  const orderstatusesref: any[] = state.data?.orderstatusesref ?? []
  const map = new Map<string, string>()
  orderstatusesref.forEach((entry: any) => {
    const v = entry?.value ?? {}
    if (v.isOrderStatus && v.value && v.lifeCycle) map.set(v.value, v.lifeCycle)
  })
  return map
}

/**
 * "Is this order currently with the driver?" — the `alreadyPushed` guard that stops
 * publish from stamping OrderStatus / AutoBillLatestAction (see buildOrderUpdateFields).
 *
 * DriverPushStatus alone is not enough: nothing ever clears it. Sending an order back
 * to Order Received writes OrderStatus 'Created' and leaves DriverPushStatus 'Pushed'
 * (dashboardSlice transitionOrder), and handleUpdates only strips the field from its
 * own merge — so a once-pushed order stayed flagged forever and publish could never
 * advance it again. It sat on a route, assigned and optimized, still reading 'Created'.
 *
 * So require BOTH: pushed AND not sitting back in the Order Received lifecycle. Keyed
 * on the lifecycle rather than the literal 'Not Started' because each project defines
 * its own status vocabulary; the lifecycle name is the contract (see lib/lifecycles).
 */
function isAlreadyPushed(fields: any, statusLifecycles: Map<string, string>): boolean {
  if (fields?.DriverPushStatus !== 'Pushed') return false
  const lifecycle = fields?.OrderStatus ? statusLifecycles.get(fields.OrderStatus) : undefined
  // Unknown status — ref not loaded, or a status outside the contract. Fall back to
  // the pushed flag alone, which is the fail-safe (suppress) answer.
  if (!lifecycle) return true
  return lifecycle !== LIFECYCLES.ORDER_RECEIVED
}

export const publishRoutes = () => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  const state = getState()
  const st: OptimizerState = state.optimizer
  const region = findRegion(state, st.region) ?? { id: st.region, name: st.region }
  const timeZone: string = (region as any).timezone ?? 'America/Toronto'
  const selectedDate: string = st.selectedDate ?? new Date().toISOString().slice(0, 10)
  const opsStatusIdMap = buildOpsStatusIdMap(state)
  const statusLifecycles = buildStatusLifecycleMap(state)
  const user: string = resolveActorEmail(null, state)
  try {
    // Collect stop ids for all non-pseudo routes, then fetch each order's current
    // OpsStatuses so we can merge them rather than clobber them.
    const stopIds = st.routes
      .filter(r => !isPseudoRoute(r.vehicle))
      .flatMap(r => r.stops.map((s: any) => s.id))

    const opsStatusesByOrderId = new Map<string, any[]>()
    // Same read also yields DriverPushStatus + OrderStatus — see isAlreadyPushed.
    // Free: this getDoc already runs for OpsStatuses.
    const alreadyPushedByOrderId = new Map<string, boolean>()
    await Promise.all(
      stopIds.map(async (id) => {
        try {
          const snap = await getDoc(doc(getProjectCollection('TMS_ORDERS'), id))
          const data = snap.data() as any
          opsStatusesByOrderId.set(id, data?.fields?.OpsStatuses ?? [])
          alreadyPushedByOrderId.set(id, isAlreadyPushed(data?.fields, statusLifecycles))
        } catch {
          opsStatusesByOrderId.set(id, [])
          // Read failed — assume pushed so the guard fails SAFE. Worst case the
          // order keeps its current status; the alternative clobbers a live one.
          alreadyPushedByOrderId.set(id, true)
        }
      })
    )

    const batch = writeBatch(db)
    for (const r of st.routes) {
      if (isPseudoRoute(r.vehicle)) continue
      r.stops.forEach((stop: any, i: number) => {
        const vehicleDriver = r.driver
          ? { firstName: r.driver.firstName, lastName: r.driver.lastName, email: r.driver.email }
          : null
        const orderFields = buildOrderUpdateFields({
          order: {
            vehicle: r.vehicle,
            serviceTime: stop.serviceTime ?? 0,
            isTruckLocked: r.isTruckLocked,
            timeLocked: !!stop.timeLocked,
            timeZone,
            // Always the board's current window — i.e. the calculated/optimized
            // schedule. See the note in publishRoutesSelective: a time lock is an
            // INPUT to the solver, never the value published.
            startWindow: stop.startWindow,
            endWindow: stop.endWindow,
            pickupDelivery: stop.pickupDelivery,
            // address: not available on optimizer stops (not in Stop shape) — skip
          },
          selectedDate,
          existingOpsStatuses: opsStatusesByOrderId.get(stop.id) ?? [],
          opsStatusIdMap,
          vehicleDriver,
          // INTENTIONAL DIVERGENCE from Vue: Vue gates includeVehicleDriver on
          // pickupDelivery !== 'Drop Off', but the single-date React optimizer
          // assigns the route's vehicle/driver to every stop physically on the
          // route (including Drop Off) — matching the pre-port React publish
          // behaviour. Do NOT change this without updating the Vue parity audit.
          includeVehicleDriver: true,
          includeAddress: false,  // address split not available on optimizer stops
          alreadyPushed: alreadyPushedByOrderId.get(stop.id) ?? false,
        })
        // Build the firestore update: prefix all fields, add Sequence
        const patch: Record<string, any> = { 'fields.Sequence': i + 1 }
        Object.entries(orderFields).forEach(([k, v]) => { patch[`fields.${k}`] = v })
        // Stamp the actor — see the note in publishRoutesSelective.
        if (user) patch['fields.User'] = user
        batch.update(doc(db, 'TMS_ORDERS', stop.id), patch)
      })
    }
    await batch.commit()
    dispatch(appendSnapshot('Published routes'))
  } catch (e: any) {
    dispatch(setError(e?.message ?? 'Failed to publish routes'))
  }
}

/**
 * Payload mirrors usePushToDriver (src/pages/Dispatch/hooks/usePushToDriver.ts):
 *   { orders: [{ id }], user: email|id, environmentName: REACT_APP_PROJECT_ID }
 * The optimizer only has normalised stops, so we send { id } objects — same
 * shape as usePushToDriver's eligible.map((o) => ({ id: o.id })).
 */
export const pushToDriver = () => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  try {
    const state = getState()
    const orders = (state.optimizer.routes as Route[])
      .filter((r) => !isPseudoRoute(r.vehicle))
      .flatMap((r) => r.stops.map((stop: any) => ({ id: stop.id })))
    const user: string = resolveActorEmail(null, state)
    const environmentName: string = process.env.REACT_APP_PROJECT_ID || ''
    await axios.post(process.env.REACT_APP_PUSH_TO_DRIVER as string, { orders, user, environmentName })
  } catch (e: any) {
    dispatch(setError(e?.message ?? 'Push to driver failed'))
  }
}

/**
 * Send an FCM "new order(s)" app push to a single driver — mirrors OrderTable's
 * sendNewOrderPushNotification (Table.tsx). One notification per driver; the body
 * varies by order count. Best-effort: failures are logged, never thrown, so a push
 * failure can't block or fail the publish.
 *
 * Unlike OrderTable, the optimizer's route.driver carries no driverAppUserName, so
 * the caller must resolve it (email -> dispatchData driverCode) before calling here.
 */
async function sendNewOrderPushNotification(
  driverAppUserName: string,
  stops: any[],
  timeZone: string,
  triggeredBy: string,
): Promise<void> {
  if (!driverAppUserName) return
  const projectId = process.env.REACT_APP_PROJECT_ID || ''
  if (!projectId) {
    console.warn('Optimizer push notification: REACT_APP_PROJECT_ID not available')
    return
  }
  try {
    const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/MOBILEAPP-sendPushNotification`
    const orderCount = stops?.length ?? 0
    const isMultiple = orderCount > 1
    const title = isMultiple ? 'New Orders Assigned' : 'New Order Assigned'
    let body: string
    if (isMultiple) {
      body = `You have ${orderCount} new orders.`
    } else {
      // Optimizer stops carry startWindow as an ISO string; interpret in the region tz.
      const startWindow = stops?.[0]?.startWindow
      const startWindowText = startWindow
        ? moment.tz(startWindow, timeZone).format('DD/MM/YYYY HH:mm')
        : ''
      body = startWindowText
        ? `You have new order on ${startWindowText}`
        : 'You have a new order assigned'
    }
    await axios.post(
      functionUrl,
      {
        driverAppUserName,
        title,
        body,
        data: { type: 'new_order', orderCount: String(orderCount) },
        triggeredBy,
      },
      { headers: { 'Content-Type': 'application/json' } },
    )
  } catch (error: any) {
    // Push notification failure shouldn't block order pushing.
    console.error('Optimizer push notification failed:', error?.response?.data ?? error?.message)
  }
}

/**
 * Selective publish — mirrors the Vue UpdateDataModal's updateOrderData logic.
 *
 * Per-stop inclusion rules:
 *  - updateAll: include every non-pseudo stop
 *  - otherwise: include if (pickupDelivery !== 'Drop Off' && vehicles.includes(r.vehicle))
 *
 * includeVehicleDriver per stop:
 *   updateAll || (pickupDelivery !== 'Drop Off' && vehicles.includes(r.vehicle))
 *
 * Address fields are never written (diverges from Vue, which has an address
 * toggle). Publish could only write back the address it read — the board doesn't
 * edit addresses (map pin moves go through persistStopGeocode, the order editor
 * writes directly) — so it added nothing, while being able to clobber an edit made
 * elsewhere after the board loaded.
 *
 * After batch: if pushes is non-empty, POST those vehicles' stop ids to the
 * push-to-driver endpoint (same shape as pushToDriver).
 */
export const publishRoutesSelective = (args: {
  vehicles: string[]
  pushes: string[]
  updateAll: boolean
}) => async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
  const { vehicles, pushes, updateAll } = args
  const state = getState()
  const st: OptimizerState = state.optimizer
  const region = findRegion(state, st.region) ?? { id: st.region, name: st.region }
  const timeZone: string = (region as any).timezone ?? 'America/Toronto'
  const selectedDate: string = st.selectedDate ?? new Date().toISOString().slice(0, 10)
  const opsStatusIdMap = buildOpsStatusIdMap(state)
  const statusLifecycles = buildStatusLifecycleMap(state)
  const user: string = resolveActorEmail(null, state)

  try {
    // Collect candidate stops across ALL routes — including the unassigned
    // buckets (ReadyToRoute / PreRoute / Unfit Stops). On "Update all" their
    // orders must be written too: buildOrderUpdateFields treats a bucket as an
    // unassigned vehicle and clears Vehicle/Driver, so an order moved back into a
    // bucket has its assignment cleared in the DB on publish. Selective publishes
    // still skip them via the `inVehicles` filter below (a bucket name is never in
    // the modal's `vehicles` set). Vue parity: flattenStopsArr flattens every
    // route and only HUB stops are excluded.
    type StopEntry = { stop: any; route: any }
    const candidateStops: StopEntry[] = []
    for (const r of st.routes) {
      for (const stop of r.stops) {
        // Hubs have no backing order document — never publish them.
        if (stop.orderType === 'HUB') continue
        // Match on the ROUTE's vehicle, not stop.vehicle. The modal selects board
        // (route) vehicle names; a stop's own `vehicle` field can drift from the
        // route it now sits in (e.g. loaded from its original fields.Vehicle, or a
        // move path that didn't restamp it). Filtering by the stale stop.vehicle
        // left candidateStops empty → Save wrote nothing.
        const inVehicles =
          updateAll ||
          (stop.pickupDelivery !== 'Drop Off' && vehicles.includes(r.vehicle))
        if (inVehicles) {
          candidateStops.push({ stop, route: r })
        }
      }
    }

    // Pre-fetch existing OpsStatuses + DriverPushStatus/OrderStatus (mirrors publishRoutes)
    const opsStatusesByOrderId = new Map<string, any[]>()
    const alreadyPushedByOrderId = new Map<string, boolean>()
    await Promise.all(
      candidateStops.map(async ({ stop }) => {
        try {
          const snap = await getDoc(doc(getProjectCollection('TMS_ORDERS'), stop.id))
          const data = snap.data() as any
          opsStatusesByOrderId.set(stop.id, data?.fields?.OpsStatuses ?? [])
          alreadyPushedByOrderId.set(stop.id, isAlreadyPushed(data?.fields, statusLifecycles))
        } catch {
          opsStatusesByOrderId.set(stop.id, [])
          // Read failed — assume pushed so the guard fails SAFE. Worst case the
          // order keeps its current status; the alternative clobbers a live one.
          alreadyPushedByOrderId.set(stop.id, true)
        }
      })
    )

    // Build driver map: vehicle name -> driver object
    const driverByVehicle = new Map<string, any>()
    for (const r of st.routes) {
      if (r.driver && r.vehicle) driverByVehicle.set(r.vehicle, r.driver)
    }

    // Collect the patches first, then commit them in batch-sized chunks below.
    const writes: Array<{ id: string; patch: Record<string, any> }> = []
    for (const { stop, route: r } of candidateStops) {
      const rawDriver = driverByVehicle.get(r.vehicle)
      const vehicleDriver = rawDriver
        ? {
            firstName: rawDriver.firstName,
            lastName: rawDriver.lastName,
            email: rawDriver.email,
          }
        : null

      const includeVehicleDriver =
        updateAll ||
        (stop.pickupDelivery !== 'Drop Off' && vehicles.includes(r.vehicle))


      const orderFields = buildOrderUpdateFields({
        order: {
          vehicle: r.vehicle,
          serviceTime: stop.serviceTime ?? 0,
          isTruckLocked: r.isTruckLocked,
          timeLocked: !!stop.timeLocked,
          timeZone,
          // The CALCULATED/OPTIMIZED window always wins over a time lock.
          //
          // A lock is an INPUT to the solver — toBackendRoutes ships timeLocked /
          // lockedStartWindow / lockedEndWindow with every stop, so the solver
          // schedules around it and the returned stop.startWindow already reflects
          // it. Publishing lockedStartWindow instead meant that whenever the solver
          // could NOT honour the lock exactly, the DB got the requested time while
          // the board card (which renders stop.startWindow) showed the real one.
          // The lock survives as the 'Time Locked' ops status, stamped from
          // order.timeLocked below.
          startWindow: stop.startWindow,
          endWindow: stop.endWindow,
          pickupDelivery: stop.pickupDelivery,
          address: stop.address,
        },
        selectedDate,
        existingOpsStatuses: opsStatusesByOrderId.get(stop.id) ?? [],
        opsStatusIdMap,
        vehicleDriver,
        includeVehicleDriver,
        // Never write Dropoff* — see the note on this thunk.
        includeAddress: false,
        // Suppresses the status rollback on a re-publish. Harmless for a stop whose
        // vehicle is also in `pushes`: the push below re-stamps the status anyway.
        alreadyPushed: alreadyPushedByOrderId.get(stop.id) ?? false,
      })

      const patch: Record<string, any> = {}
      Object.entries(orderFields).forEach(([k, v]) => { patch[`fields.${k}`] = v })
      // Stamp the actor. The ONWRITE trigger's history row reads its UserEmail
      // straight off the order's `fields.User` (functions createBillingHistory),
      // so a publish that doesn't write it attributes the entry to whoever
      // touched the order last — the push endpoint, a form save, the importer.
      // Every other portal write path stamps it the same way (OrderDisplay,
      // billingState quick actions). Skip when the store has no identity rather
      // than blanking a good value.
      if (user) patch['fields.User'] = user
      writes.push({ id: stop.id, patch })
    }

    // Firestore commits at most FIRESTORE_BATCH_LIMIT writes per batch and a batch is
    // atomic, so a busy day sent as one batch failed wholesale and wrote nothing.
    // Commit sequentially: a later chunk failing still leaves the earlier ones
    // applied, and the throw surfaces to the modal rather than reporting success.
    for (let i = 0; i < writes.length; i += FIRESTORE_BATCH_LIMIT) {
      const batch = writeBatch(db)
      for (const { id, patch } of writes.slice(i, i + FIRESTORE_BATCH_LIMIT)) {
        batch.update(doc(db, 'TMS_ORDERS', id), patch)
      }
      await batch.commit()
    }
    dispatch(appendSnapshot('Selective publish'))

    // Push to drivers for chosen vehicles
    if (pushes.length > 0) {
      const pushSet = new Set(pushes)
      const pushedEntries = candidateStops.filter(
        ({ route: r }) => pushSet.has(r.vehicle) && !isPseudoRoute(r.vehicle),
      )
      const pushOrders = pushedEntries.map(({ stop }) => ({ id: stop.id }))
      if (pushOrders.length > 0) {
        const environmentName: string = process.env.REACT_APP_PROJECT_ID || ''
        let pushSucceeded = false
        try {
          await axios.post(process.env.REACT_APP_PUSH_TO_DRIVER as string, { orders: pushOrders, user, environmentName })
          pushSucceeded = true
        } catch (e: any) {
          // Push failure is non-fatal — batch already committed
          console.error('publishRoutesSelective: push to driver failed', e?.message)
        }

        // FCM app push per driver — mirrors OrderTable "Push (With Notification)".
        // Only after a successful push-to-driver call (matches OrderTable ordering).
        // route.driver has no app username, so join its email to the dispatch driver
        // list's driverCode (= driverAppUserName). Best-effort; never blocks publish.
        if (pushSucceeded) {
          const emailToDriverCode = new Map<string, string>()
          for (const d of (state?.dispatchData?.drivers ?? [])) {
            const email = (d?.value?.email ?? '').toString().trim().toLowerCase()
            const code = (d?.value?.driverCode ?? '').toString()
            if (email && code) emailToDriverCode.set(email, code)
          }

          const stopsByDriver = new Map<string, any[]>()
          for (const { stop, route: r } of pushedEntries) {
            const email = (r?.driver?.email ?? '').toString().trim().toLowerCase()
            const driverAppUserName = email ? emailToDriverCode.get(email) : ''
            if (!driverAppUserName) continue
            if (!stopsByDriver.has(driverAppUserName)) stopsByDriver.set(driverAppUserName, [])
            stopsByDriver.get(driverAppUserName)!.push(stop)
          }

          await Promise.allSettled(
            Array.from(stopsByDriver.entries()).map(([driverAppUserName, driverStops]) =>
              sendNewOrderPushNotification(driverAppUserName, driverStops, timeZone, user),
            ),
          )
        }
      }
    }
  } catch (e: any) {
    dispatch(setError(e?.message ?? 'Failed to publish routes (selective)'))
    throw e
  }
}

export default slice.reducer
